import { Button, Form, Input, InputNumber, Space, Table, Tooltip, Typography } from "antd";
import { Dispatch, FC, SetStateAction, useState } from "react";
import { ColumnsType } from "antd/es/table";
import { DeleteOutlined, EditOutlined, SaveOutlined } from "@ant-design/icons";
import { AgentByIdResponse, GetOrderDetailData, OrderProductsTemporaryItem } from "services/interface";
import { numberFormat } from "utils/nuberFormat";

const { TextArea } = Input;

interface OrderReserveProductTableProps {
  data: OrderProductsTemporaryItem[];
  setData: Dispatch<SetStateAction<OrderProductsTemporaryItem[]>>;
  canDelete: boolean;
  showStatusColumn: boolean;
  editingKey: string;
  setEditingKey: Dispatch<SetStateAction<string>>;
  currentSelected: string;
  setCurrentSelected: Dispatch<SetStateAction<string>>;
  refType: string;
  userData: AgentByIdResponse | undefined;
  isKsher?: boolean;
  item?: GetOrderDetailData;
}

interface EditableCellProps {
  editing: boolean;
  dataIndex: string;
  title: string;
  inputType: 'number' | 'text' | 'textarea';
  record: OrderProductsTemporaryItem;
  index: number;
  children: React.ReactNode;
  isKsher: boolean
}

const EditableCell: React.FC<EditableCellProps> = ({
  editing,
  dataIndex,
  title,
  inputType,
  record,
  index,
  children,
  isKsher,
  ...restProps
}) => {
  console.log('isKsher', isKsher);

  const inputNode = inputType === 'number' ? <InputNumber
    disabled={isKsher}
  /> : inputType === 'textarea' ? <TextArea rows={1} /> : <Input />;

  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={dataIndex}
          style={{ margin: 0 }}
          rules={[
            {
              validator: async (_, value) => {
                if (dataIndex === 'qty' && (!value || value < 1)) {
                  throw new Error('กรุณาใส่ qty มากกว่า 0');
                }
              },
            },
          ]}
        >
          {inputNode}
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};

const OrderReserveProductTable: FC<OrderReserveProductTableProps> = ({
  data,
  setData,
  canDelete,
  showStatusColumn,
  editingKey,
  setEditingKey,
  currentSelected,
  setCurrentSelected,
  refType,
  userData,
  isKsher,
  item
}) => {
  const [form] = Form.useForm();

  const isEditing = (record: OrderProductsTemporaryItem) => record.sku_id === editingKey;

  const edit = (record: Partial<OrderProductsTemporaryItem> & { sku_id: React.Key }) => {
    form.setFieldsValue({ qty: '', comment_text: '', ...record });
    setEditingKey(record.sku_id as string);
    setCurrentSelected(record.sku_id as string);
  };

  const cancel = () => {
    setEditingKey('');
    setCurrentSelected('');
  };

  const handleDelete = (record: OrderProductsTemporaryItem) => {
    const newData = data.filter(item => item.sku_id !== record.sku_id);
    if (currentSelected === record.sku_id) {
      setEditingKey('');
      setCurrentSelected('');
    }
    setData(newData);
  };

  const save = async (key: React.Key) => {
    try {
      const row = (await form.validateFields()) as OrderProductsTemporaryItem;

      const newData = [...data];
      const index = newData.findIndex((item) => key === item.sku_id);
      if (index > -1) {
        const item = newData[index];
        newData.splice(index, 1, {
          ...item,
          ...row,
        });
        setData(newData);
        setEditingKey('');
        setCurrentSelected('');
      } else {
        newData.push(row);
        setData(newData);
        setEditingKey('');
        setCurrentSelected('');
      }
    } catch (errInfo) {
      console.log('Validate Failed:', errInfo);
    }
  };



  const columns = [
    {
      title: "รายการสินค้า",
      dataIndex: "product_name",
      render: (_: any, record: OrderProductsTemporaryItem) => {
        return (
          <div style={{ display: "flex", flexDirection: "row" }}>
            <img
              src={record.sku_image}
              alt={record.sku_image}
              style={{ width: 40, height: 40, marginRight: 10 }}
            />
            <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              <Typography.Paragraph style={{ marginBottom: 0 }}>{record.product_name}</Typography.Paragraph>
              <Typography.Paragraph style={{ fontWeight: 500, marginBottom: 0 }}>{record.sku_name}</Typography.Paragraph>
            </div>
          </div>
        );
      },
    },
    {
      title: "โน๊ตผู้ประกอบการ",
      dataIndex: "comment_text",
      width: "300px",
      editable: true,
      render: (_: any, record: OrderProductsTemporaryItem) => {
        return (
          <Tooltip title={"สามารถ Edit ได้"}>
          <Typography.Paragraph style={{ marginBottom: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", width: 280 }}>{record.comment_text}</Typography.Paragraph>
          </Tooltip>
        );
      },
    },
    {
      title: "จำนวน",
      dataIndex: "qty",
      width: "100px",
      editable: true,
    },
    {
      title: "ราคาต่อหน่วย",
      dataIndex: "sale_price",
      width: "100px",
      render: (_: any, record: OrderProductsTemporaryItem) => {
        return (
          <Typography.Paragraph style={{ marginBottom: 0 }}>{numberFormat(Number(refType === "retail" ? record.wholesale_price : record.factory_price))}</Typography.Paragraph>
        );
      },
    },
    {
      title: "ราคารวม",
      width: "100px",
      render: (_: any, record: OrderProductsTemporaryItem) => {
        const salePrice = refType === "retail" ? record.wholesale_price : record.factory_price;
        return (
          <Typography.Paragraph style={{ marginBottom: 0 }}>{numberFormat(Number(salePrice * record.qty))}</Typography.Paragraph>
        );
      },
    },
    {
      title: 'เครื่องมือ',
      dataIndex: 'operation',
      width: "120px",
      render: (_: any, record: OrderProductsTemporaryItem) => {
        const editable = isEditing(record);
        return editable ? (
          <span>
            <Button
              type="default"
              icon={<SaveOutlined />}
              style={{ width: '100px', marginRight: '5px' }}
              onClick={() => save(record.sku_id)}
            >
              Save
            </Button>
            {canDelete && (
              <Button
                type="primary"
                danger
                icon={<DeleteOutlined />}
                style={{ width: '100px', marginRight: '5px' }}
                onClick={() => handleDelete(record)}
              >
                Delete
              </Button>
            )}
          </span>
        ) : (
          <div>
            <Button
              type="default"
              icon={<EditOutlined />}
              style={{ width: '100px', marginRight: '5px' }}
              disabled={editingKey !== ''} onClick={() => edit(record)}
            >
              Edit
            </Button>
            {canDelete && (
              <Button
                type="primary"
                danger
                icon={<DeleteOutlined />}
                style={{ marginRight: '5px' }}
                onClick={() => handleDelete(record)}
              >
                Delete
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  const mergedColumns: ColumnsType<OrderProductsTemporaryItem> = columns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: OrderProductsTemporaryItem) => ({
        record,
        inputType: col.dataIndex === 'qty' ? 'number' : col.dataIndex === 'comment_text' ? 'textarea' : 'text',
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
        isKsher,
      }),
    };
  });

  const totalProductPriceTable = data.reduce((total, record) => total + ( (refType === "retail") ? record.wholesale_price : record.factory_price) * record.qty, 0);

  const totalPrivilgePriceTable = data.reduce((total, record) => total + (record.vip_price * record.qty), 0);

  const totalDiscountCodePriceTable = data.reduce((total, record) => total + (record.factory_price * record.qty), 0);

  console.log('userData--->', userData);

  return (
    <div >
      <Form form={form} component={false}>
        <Table
        
          components={{
            body: {
              cell: EditableCell,
            },
          }}
          bordered
          dataSource={data}
          columns={mergedColumns}
          rowClassName="editable-row"
          pagination={{
            onChange: cancel,
          }}
          scroll={{ x: 768 }}
        />
      </Form>
      {userData && userData.is_vip &&
        <Typography.Title level={5} style={{ padding: "0px 10px 0px 0px", margin: 0, display: "flex", justifyContent: "flex-end", color: "red" }}>
          ส่วนลดสิทธิพิเศษ: {numberFormat(totalProductPriceTable - totalPrivilgePriceTable)} บาท
        </Typography.Title>
      }

      {item && item?.discount_code &&
        <Typography.Title level={5} style={{ padding: "0px 10px 0px 0px", margin: 0, display: "flex", justifyContent: "flex-end" }}>
          ส่วนลดสิทธิพิเศษ: {numberFormat(totalProductPriceTable - totalDiscountCodePriceTable)} บาท
        </Typography.Title>
      }

      <Typography.Title level={5} style={{ padding: "0px 10px 0px 0px", margin: 0, display: "flex", justifyContent: "flex-end" }}>
        จำนวนเงินรวมทั้งสิ้น: {numberFormat(totalProductPriceTable)} บาท
      </Typography.Title>
    </div>
  );
};

export default OrderReserveProductTable;